// The animated 404 illustration: a cyclist's front wheel pops off, rolls
// away and settles into the gap between the two 4s as the "0".
//
// Only the numerals, the road line and the dashed placeholder "0" are in the
// server-rendered SVG — the page has to read as "404" before (or without) any
// JavaScript. Everything else is drawn here on mount, because the scenery is
// several hundred small shapes and inlining them would add ~50 KB to every
// 404 response for something purely decorative.
//
// Returns { replay, destroy }. The animation loop stops itself once the scene
// has settled, so an idle 404 tab isn't burning a requestAnimationFrame
// forever; clicking the wheel or calling replay() starts it again.

const NS = 'http://www.w3.org/2000/svg'

// Brand tokens where they exist (see assets/_variables.scss); the rest are
// illustration-only tints of them.
const C = {
  blue: '#3e4cd4',
  navy: '#1b2064',
  navyFar: '#11154a',
  yellow: '#f5c13b',
  lav: '#dde0f8',
  lavMid: '#c9cdf3',
  spoke: '#9aa0e8',
  skin: '#f4cdb0',
  skinFar: '#d9aa8c',
  cloud: '#eef0fc',
}

const W = 1200
const GROUND = 440
const R = 52 // wheel radius

function el(tag, attrs = {}, parent) {
  const n = document.createElementNS(NS, tag)
  for (const k in attrs) n.setAttribute(k, attrs[k])
  if (parent) parent.appendChild(n)
  return n
}

// Seeded, so the skyline is the same on every visit.
function seededRandom(seed) {
  return () => (seed = (seed * 16807) % 2147483647) / 2147483647
}

// Each parallax layer is drawn twice, side by side, so it can scroll
// seamlessly by translating modulo W.
function tile(parent, draw) {
  for (let copy = 0; copy < 2; copy++) {
    draw(el('g', { transform: `translate(${copy * W} 0)` }, parent))
  }
}

function drawScenery(layers, rng) {
  const cloudData = [
    [120, 70, 1],
    [520, 40, 0.8],
    [880, 95, 1.1],
  ]
  tile(layers.clouds, (g) =>
    cloudData.forEach(([x, y, s]) => {
      const c = el(
        'g',
        { transform: `translate(${x} ${y}) scale(${s})`, fill: C.cloud },
        g
      )
      el('circle', { cx: 0, cy: 0, r: 22 }, c)
      el('circle', { cx: 26, cy: -10, r: 28 }, c)
      el('circle', { cx: 56, cy: 2, r: 20 }, c)
      el('rect', { x: 0, y: 0, width: 56, height: 22 }, c)
    })
  )

  const skyline = []
  for (let x = 0; x < W; ) {
    const w = 40 + rng() * 70
    const h = 50 + rng() * 120
    skyline.push({ x, w, h, roof: rng() })
    x += w + (rng() > 0.7 ? 14 : 0)
  }
  tile(layers.skyline, (g) =>
    skyline.forEach((b) => {
      const top = GROUND - b.h
      const mid = b.x + b.w / 2
      el('rect', { x: b.x, y: top, width: b.w, height: b.h, fill: C.lav }, g)
      if (b.roof > 0.82) {
        el(
          'path',
          {
            d: `M${mid - 10} ${top} L${mid} ${top - 46} L${mid + 10} ${top}Z`,
            fill: C.lav,
          },
          g
        )
      } else if (b.roof > 0.66) {
        el('circle', { cx: mid, cy: top, r: b.w / 3, fill: C.lav }, g)
      }
      for (let wy = top + 14; wy < GROUND - 16; wy += 22) {
        for (let wx = b.x + 9; wx < b.x + b.w - 12; wx += 16) {
          el(
            'rect',
            { x: wx, y: wy, width: 6, height: 10, rx: 1, fill: C.lavMid },
            g
          )
        }
      }
    })
  )

  tile(layers.trees, (g) => {
    for (let x = 40; x < W; x += 130 + Math.round(rng() * 80)) {
      if (rng() > 0.78) {
        // Lamp post
        el(
          'line',
          {
            x1: x,
            y1: GROUND,
            x2: x,
            y2: GROUND - 92,
            stroke: C.navy,
            'stroke-width': 3,
          },
          g
        )
        el(
          'path',
          {
            d: `M${x} ${GROUND - 92} q0 -8 12 -8`,
            stroke: C.navy,
            'stroke-width': 3,
            fill: 'none',
          },
          g
        )
        el('circle', { cx: x + 14, cy: GROUND - 97, r: 5, fill: C.yellow }, g)
      } else {
        const h = 44 + rng() * 36
        const r = 16 + rng() * 12
        const col = rng() > 0.5 ? C.blue : C.spoke
        el(
          'line',
          {
            x1: x,
            y1: GROUND,
            x2: x,
            y2: GROUND - h,
            stroke: C.navy,
            'stroke-width': 3,
          },
          g
        )
        el('circle', { cx: x, cy: GROUND - h - r * 0.6, r, fill: col }, g)
      }
    }
  })

  tile(layers.dashes, (g) => {
    for (let x = 0; x < W; x += 80) {
      el(
        'rect',
        { x, y: 454, width: 40, height: 5, rx: 2.5, fill: C.lavMid },
        g
      )
    }
  })
}

// Returns the spoke group, which is what gets rotated.
function makeWheel(parent) {
  const g = el('g', {}, parent)
  el('circle', { r: R - 6, fill: '#fff' }, g)
  const spokes = el('g', {}, g)
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2
    el(
      'line',
      {
        x1: 0,
        y1: 0,
        x2: Math.cos(a) * (R - 6),
        y2: Math.sin(a) * (R - 6),
        stroke: C.spoke,
        'stroke-width': 1.3,
      },
      spokes
    )
  }
  el(
    'circle',
    { r: R - 6, fill: 'none', stroke: C.lavMid, 'stroke-width': 3 },
    g
  )
  el('circle', { r: R, fill: 'none', stroke: C.navy, 'stroke-width': 9 }, g)
  // The valve is what makes the spin readable.
  el('circle', { cx: 0, cy: -(R - 8), r: 3, fill: C.yellow }, spokes)
  el('circle', { r: 6, fill: C.navy }, g)
  return spokes
}

// Bike and rider, in local coordinates with the rear hub at 0,0.
const BB = { x: 70, y: 8 }
const HIP = { x: 46, y: -94 }
const CRANK = 22
const THIGH = 64
const SHIN = 62

function drawBike(bike) {
  const round = { 'stroke-linecap': 'round' }
  const leg = (col, shinCol) => ({
    thigh: el('line', { stroke: col, 'stroke-width': 16, ...round }, bike),
    shin: el('line', { stroke: shinCol, 'stroke-width': 11, ...round }, bike),
    shoe: el('line', { stroke: C.navy, 'stroke-width': 9, ...round }, bike),
  })

  const farLeg = leg(C.navyFar, C.skinFar)
  const farCrank = el(
    'line',
    { stroke: C.navy, 'stroke-width': 5, ...round },
    bike
  )
  const rearSpokes = makeWheel(el('g', {}, bike))

  const frame = el(
    'g',
    {
      stroke: C.yellow,
      'stroke-width': 7,
      'stroke-linejoin': 'round',
      fill: 'none',
      ...round,
    },
    bike
  )
  el('path', { d: `M0 0 L${BB.x} ${BB.y} L50 -70 Z` }, frame)
  el('path', { d: `M${BB.x} ${BB.y} L152 -62 L148 -80 L50 -72` }, frame)
  el(
    'line',
    {
      x1: 152,
      y1: -62,
      x2: 170,
      y2: 0,
      stroke: C.yellow,
      'stroke-width': 6,
      ...round,
    },
    bike
  )
  el(
    'line',
    {
      x1: 50,
      y1: -72,
      x2: 46,
      y2: -86,
      stroke: C.navy,
      'stroke-width': 5,
      ...round,
    },
    bike
  )
  el(
    'path',
    {
      d: 'M30 -90 Q46 -94 62 -89',
      stroke: C.navy,
      'stroke-width': 7,
      fill: 'none',
      ...round,
    },
    bike
  )
  el(
    'path',
    {
      d: 'M148 -80 L162 -86 Q174 -86 172 -76 Q170 -68 162 -70',
      stroke: C.navy,
      'stroke-width': 5,
      'stroke-linejoin': 'round',
      fill: 'none',
      ...round,
    },
    bike
  )
  el(
    'circle',
    {
      cx: BB.x,
      cy: BB.y,
      r: 12,
      fill: 'none',
      stroke: C.navy,
      'stroke-width': 4,
    },
    bike
  )

  // Torso, head, arm
  el(
    'line',
    {
      x1: HIP.x,
      y1: HIP.y,
      x2: 118,
      y2: -140,
      stroke: C.blue,
      'stroke-width': 28,
      ...round,
    },
    bike
  )
  el(
    'line',
    {
      x1: 62,
      y1: -105,
      x2: 104,
      y2: -132,
      stroke: C.yellow,
      'stroke-width': 5,
      ...round,
    },
    bike
  )
  const head = el('g', { transform: 'translate(142 -154)' }, bike)
  el('circle', { r: 14, fill: C.skin }, head)
  el(
    'path',
    { d: 'M-16 2 Q-16 -18 4 -18 Q18 -17 20 -2 L6 -4 Z', fill: C.yellow },
    head
  )
  el(
    'line',
    {
      x1: 4,
      y1: -4,
      x2: 17,
      y2: -3,
      stroke: C.navy,
      'stroke-width': 4,
      ...round,
    },
    head
  )
  el(
    'polyline',
    {
      points: '118,-140 138,-110 166,-84',
      stroke: C.blue,
      'stroke-width': 11,
      'stroke-linejoin': 'round',
      fill: 'none',
      ...round,
    },
    bike
  )
  el(
    'line',
    {
      x1: 144,
      y1: -104,
      x2: 166,
      y2: -84,
      stroke: C.skin,
      'stroke-width': 9,
      ...round,
    },
    bike
  )

  const nearLeg = leg(C.navy, C.skin)
  const nearCrank = el(
    'line',
    { stroke: C.navy, 'stroke-width': 5, ...round },
    bike
  )

  return { farLeg, farCrank, nearLeg, nearCrank, rearSpokes, head }
}

function setLine(n, x1, y1, x2, y2) {
  n.setAttribute('x1', x1)
  n.setAttribute('y1', y1)
  n.setAttribute('x2', x2)
  n.setAttribute('y2', y2)
}

// Two-bone IK from the hip to the pedal.
function placeLeg(leg, crankLine, angle) {
  const fx = BB.x + Math.cos(angle) * CRANK
  const fy = BB.y + Math.sin(angle) * CRANK
  const dx = fx - HIP.x
  const dy = fy - HIP.y
  const d = Math.min(Math.hypot(dx, dy), THIGH + SHIN - 0.5)
  const a = Math.atan2(dy, dx)
  const b = Math.acos((THIGH * THIGH + d * d - SHIN * SHIN) / (2 * THIGH * d))
  const kx = HIP.x + Math.cos(a - b) * THIGH
  const ky = HIP.y + Math.sin(a - b) * THIGH
  setLine(leg.thigh, HIP.x, HIP.y, kx, ky)
  setLine(leg.shin, kx, ky, fx, fy)
  setLine(leg.shoe, fx - 4, fy + 2, fx + 10, fy + 2)
  setLine(crankLine, BB.x, BB.y, fx, fy)
}

// Timeline constants (seconds, SVG units).
const BIKE_X = 200
const HUB_Y = GROUND - R
const V = 290 // ride speed
const POP = 2.5 // when the front wheel comes off
const DECEL = 1.1
const ZERO = { x: 600, y: 122, s: 1.4 } // where the wheel becomes the "0"
const SETTLED = 6 // after this, with no spin boost, the loop stops

const clamp01 = (v) => Math.max(0, Math.min(1, v))
const ease = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2)
const bounce = (p) => {
  const n = 7.5625
  const d = 2.75
  if (p < 1 / d) return n * p * p
  if (p < 2 / d) return n * (p -= 1.5 / d) * p + 0.75
  if (p < 2.5 / d) return n * (p -= 2.25 / d) * p + 0.9375
  return n * (p -= 2.625 / d) * p + 0.984375
}
const scrollAt = (t) => {
  if (t < POP) return V * t
  const k = Math.min(t - POP, DECEL)
  return V * POP + V * (k - (k * k) / (2 * DECEL))
}

function frontWheelAt(t) {
  const u = t - POP
  if (u < 0) return { x: BIKE_X + 170, y: HUB_Y, s: 1 }
  if (u < 0.5) {
    const p = u / 0.5
    return { x: BIKE_X + 170 + 110 * p, y: HUB_Y - 64 * 4 * p * (1 - p), s: 1 }
  }
  if (u < 0.85) {
    const p = (u - 0.5) / 0.35
    return { x: 480 + 50 * p, y: HUB_Y - 18 * 4 * p * (1 - p), s: 1 }
  }
  if (u < 1.8) {
    const p = (u - 0.85) / 0.95
    const e = ease(p)
    return {
      x: 530 + (ZERO.x - 530) * e,
      y: HUB_Y + (ZERO.y - HUB_Y) * e - 80 * Math.sin(Math.PI * p),
      s: 1 + (ZERO.s - 1) * e,
    }
  }
  const v = u - 1.8
  return {
    x: ZERO.x,
    y: ZERO.y,
    s: ZERO.s + 0.09 * Math.exp(-6 * v) * Math.sin(18 * v),
  }
}

export function createErrorScene(svg, { reduced = false, onSettled } = {}) {
  const q = (name) => svg.querySelector(`[data-scene="${name}"]`)
  const layers = {
    clouds: q('clouds'),
    skyline: q('skyline'),
    trees: q('trees'),
    dashes: q('dashes'),
  }
  const puffs = q('puffs')
  const bubble = q('bubble')
  const ghost = q('ghost')
  const wheel0 = q('wheel')
  const bike = q('bike')

  drawScenery(layers, seededRandom(42))
  const parts = drawBike(bike)
  const frontSpokes = makeWheel(wheel0)
  const parallax = [
    [layers.clouds, 0.05],
    [layers.skyline, 0.18],
    [layers.trees, 0.5],
    [layers.dashes, 1],
  ]

  let start, last, frontRot, boost, fired, activePuffs
  let raf = null

  function reset() {
    start = performance.now()
    last = start
    frontRot = 0
    boost = 0
    fired = {}
    activePuffs = []
    puffs.replaceChildren()
  }

  function puff(x, y, size = 1) {
    const g = el('g', {}, puffs)
    const dots = [-14, 0, 14].map((ox) =>
      el('circle', { cx: x + ox * size, cy: y - 4, r: 4, fill: C.lavMid }, g)
    )
    activePuffs.push({ g, dots, born: performance.now(), size })
  }

  function render(now) {
    const t = reduced ? 8 : (now - start) / 1000
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now
    const u = t - POP
    const s = scrollAt(t)

    parallax.forEach(([g, k]) =>
      g.setAttribute('transform', `translate(${-((s * k) % W)} 0)`)
    )

    // Bike
    const crank = s / 38
    const bob = u < 0 ? 1.6 * Math.sin(crank * 2) : 0
    const uu = Math.max(0, u - 0.08)
    const pitch = u < 0 ? 0 : 17 * (1 - Math.exp(-7 * uu) * Math.cos(11 * uu))
    bike.setAttribute(
      'transform',
      `translate(${BIKE_X} ${HUB_Y + bob}) rotate(${pitch} 0 ${R})`
    )
    parts.rearSpokes.setAttribute('transform', `rotate(${(s / R) * 57.3})`)
    placeLeg(parts.farLeg, parts.farCrank, crank + Math.PI)
    placeLeg(parts.nearLeg, parts.nearCrank, crank)
    parts.head.setAttribute(
      'transform',
      `translate(142 -154) rotate(${
        u > 1.6 ? -14 * clamp01((u - 1.6) / 0.4) : 0
      })`
    )

    // Front wheel
    const baseOmega = V / R
    // Coasts to a standstill once it has landed, so stopping the loop at
    // SETTLED doesn't freeze it mid-spin.
    const omega = u < 0.85 ? baseOmega : baseOmega * Math.exp(-1.6 * (u - 0.85))
    boost *= Math.exp(-1.8 * dt)
    frontRot += ((reduced ? 0 : omega) + boost) * dt
    const fw = frontWheelAt(t)
    wheel0.setAttribute(
      'transform',
      `translate(${fw.x} ${fw.y + (u < 0 ? bob : 0)}) scale(${fw.s})`
    )
    frontSpokes.setAttribute('transform', `rotate(${frontRot * 57.3})`)
    ghost.setAttribute('opacity', 1 - clamp01((u - 1.6) / 0.25))

    // One-off moments
    const once = (key, cond, fn) => {
      if (cond && !fired[key]) {
        fired[key] = true
        fn()
      }
    }
    if (!reduced) {
      once('pop', u >= 0, () => puff(BIKE_X + 170, GROUND))
      once('fork', u >= 0.3, () => puff(BIKE_X + 178, GROUND, 1.3))
      once('hop', u >= 0.5, () => puff(480, GROUND, 0.7))
    }
    once('done', u >= 2.2, () => onSettled && onSettled())

    activePuffs = activePuffs.filter((p) => {
      const a = (now - p.born) / 600
      if (a >= 1) {
        p.g.remove()
        return false
      }
      p.dots.forEach((d, i) => {
        d.setAttribute('r', (4 + 12 * a) * p.size)
        d.setAttribute('cx', +d.getAttribute('cx') + (i - 1) * 0.6)
      })
      p.g.setAttribute('opacity', 1 - a)
      return true
    })

    // Question bubble over the rider
    const bp = clamp01((u - 1.9) / 0.35)
    const bobY = reduced ? 0 : 4 * Math.sin(t * 2.2) * bp
    bubble.setAttribute('opacity', bp)
    bubble.setAttribute(
      'transform',
      `translate(${BIKE_X + 238} ${244 - bobY}) scale(${
        0.4 + 0.6 * (bp < 1 ? bounce(bp) : 1)
      })`
    )

    const idle = (reduced || u >= SETTLED) && boost < 0.05
    raf = idle ? null : requestAnimationFrame(render)
  }

  function run() {
    if (raf === null) {
      last = performance.now()
      raf = requestAnimationFrame(render)
    }
  }

  // A little easter egg: clicking the settled "0" gives it a spin.
  const spin = () => {
    boost += 14
    run()
  }
  wheel0.addEventListener('click', spin)

  reset()
  run()

  return {
    replay() {
      reset()
      run()
    },
    destroy() {
      if (raf !== null) cancelAnimationFrame(raf)
      raf = null
      wheel0.removeEventListener('click', spin)
    },
  }
}
